/**
 * What the screen remembers about an upload it queued: when it asked, and what the row's server-written
 * timestamp was at that moment.
 *
 * ⛔ Two values on two different clocks, and they must not be mixed. `queuedAt` is this browser's clock and
 * only ever feeds a DURATION - the age thresholds on the page - which compares that clock against itself.
 * `baselineUpdatedOn` is the server's, and is only ever compared against another server value. Comparing
 * the two directly leaves a browser running minutes ahead seeing every attempt as unanswered forever, and
 * one running behind calling every attempt answered the instant it was queued.
 *
 * ⛔ `null` and `undefined` are NOT the same baseline. `null` means the server has never written the row,
 * which a first attempt answers by writing it. `undefined` means this screen could not read the row at all -
 * it was queued from another page - and collapsing that to `null` makes any row with an earlier attempt look
 * answered on the first response that includes it, withdrawing the duplicate-send warning and stopping the
 * poll on a transfer that is still running.
 */
export type QueuedAttempt = {
  queuedAt: number;
  baselineUpdatedOn: number | null | undefined;
};

export const updatedOnOf = (file: { updatedOn?: Date }) =>
  file.updatedOn ? file.updatedOn.getTime() : null;

/**
 * Whether the attempt this screen queued has reported back.
 *
 * ⛔ The signal is that the SERVER REWROTE THE ROW, never that the status looks final. Status cannot answer
 * it: delivery writes Pending at export time only, so a retried row reads Failed for the whole transfer and
 * a retry that fails again leaves it reading Failed afterwards. Treating Failed as settled dropped the
 * attempt on the next refetch, which took its own block and warning with it and stopped the poll - so a
 * retry that SUCCEEDED went on showing Failed with the stale error until the operator changed a filter.
 *
 * A row nobody queued has nothing in flight and counts as answered, which is what keeps it out of the poll.
 * An attempt whose baseline is unknown never answers, so it holds its warning and its poll until the age
 * limit retires it - the conservative side of the error for a warning about sending a file twice.
 */
export const hasAnswered = (
  attempt: QueuedAttempt | undefined,
  file: { updatedOn?: Date }
) =>
  attempt === undefined ||
  (attempt.baselineUpdatedOn !== undefined &&
    updatedOnOf(file) !== attempt.baselineUpdatedOn);

/**
 * Which sentence the operator gets after a bulk upload, and how loudly.
 *
 * Three outcomes, and the order matters: something the service refused to queue outranks something this
 * screen skipped, because the first is a failure and the second is the screen doing its job. Only a run
 * where nothing was refused and nothing was skipped is a plain success.
 */
export const uploadOutcome = (
  queuedCount: number,
  notQueuedCount: number,
  skippedCount: number,
  refusedNames: string[] = []
) => {
  if (notQueuedCount > 0) {
    // Naming them is the whole point: the selection is cleared by the time this is read, and a refused row
    // is indistinguishable on screen from a queued one, so "select those" asks for something the operator
    // cannot do. Ids queued from another page have no row to name, hence the count-only wording.
    return refusedNames.length > 0
      ? {
          messageId: "exportedScormFilesUploadPartiallyQueuedNamed",
          values: { queuedCount, notQueuedCount, refusedNames: refusedNames.join(", ") },
          isWarning: true,
        }
      : {
          messageId: "exportedScormFilesUploadPartiallyQueued",
          values: { queuedCount, notQueuedCount },
          isWarning: true,
        };
  }

  if (skippedCount > 0) {
    return {
      messageId: "exportedScormFilesUploadPartiallySkipped",
      values: { queuedCount, skippedCount },
      isWarning: true,
    };
  }

  return {
    messageId: "exportedScormFilesUploadQueued",
    values: { queuedCount },
    isWarning: false,
  };
};
